/**
 * Real session-restoration gate.
 *
 * The gate mirrors AuthProvider's existing lifecycle exactly. It introduces
 * no timer, simulated progress, engine status, or authentication side effect.
 */
import { useState, type ReactNode } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";

import { useAuth } from "../../auth";
import { reducedMotionMediaQuery } from "../../theme/motionTokens";
import { GlassSurface } from "./GlassSurface";

export interface AppBootGateProps {
	children: ReactNode;
}

export function AppBootGate({ children }: AppBootGateProps) {
	const { status, actions } = useAuth();
	const [retrying, setRetrying] = useState(false);
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);

	const retryRestoration = async () => {
		setRetrying(true);
		try {
			await actions.refresh();
		} catch {
			// AuthProvider owns the error state. Keep the notice available if an
			// alternate provider implementation rejects instead of returning false.
		} finally {
			setRetrying(false);
		}
	};

	if (status === "loading") {
		return (
			<Box
				component="main"
				aria-busy="true"
				data-motion={prefersReducedMotion ? "reduced" : "full"}
				sx={{
					position: "relative",
					zIndex: 1,
					minHeight: "100vh",
					display: "grid",
					placeItems: "center",
					p: 3,
				}}
			>
				<GlassSurface
					role="status"
					aria-live="polite"
					intensity="strong"
					depth="floating"
					sx={{
						width: "min(100%, 420px)",
						p: { xs: 3, sm: 4 },
					}}
				>
					<Stack spacing={2.5} alignItems="center" textAlign="center">
						<CircularProgress
							size={34}
							thickness={3.4}
							aria-hidden="true"
							sx={(theme) => ({
								color: theme.palette.workspace.green,
								[reducedMotionMediaQuery]: {
									animation: "none",
									"& .MuiCircularProgress-circle": {
										animation: "none",
									},
								},
							})}
						/>
						<Box>
							<Typography variant="h6" component="p">
								Restoring your secure session
							</Typography>
							<Typography
								variant="body2"
								sx={(theme) => ({
									mt: 0.75,
									color: theme.palette.workspace.textSecondary,
								})}
							>
								Checking the signed-in session for this browser.
							</Typography>
						</Box>
					</Stack>
				</GlassSurface>
			</Box>
		);
	}

	return (
		<>
			{status === "error" && (
				<Box
					component="aside"
					aria-label="Session restoration notice"
					sx={(theme) => ({
						position: "fixed",
						zIndex: theme.zIndex.snackbar,
						top: { xs: 12, sm: 20 },
						left: "50%",
						width: "min(calc(100% - 24px), 620px)",
						transform: "translateX(-50%)",
					})}
				>
					<Alert
						severity="warning"
						variant="filled"
						action={
							<Button
								color="inherit"
								size="small"
								disabled={retrying}
								onClick={() => void retryRestoration()}
							>
								{retrying ? "Retrying…" : "Retry session"}
							</Button>
						}
					>
						We could not restore the session. You can continue or retry.
					</Alert>
				</Box>
			)}
			{children}
		</>
	);
}
